import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBearingOptions } from "./olePatrolsUtils";
import { getSessionData } from "redux/actions";
import { notification, Select, Tooltip } from "antd";
import moment from "moment";
import { calculateBearingText } from "components/util/Geometry";
import StandAloneVideoOrImageComponent from "components/display/StandAloneVideoOrImageComponent";
import OBCSpinner from "components/util/OBC";
import _ from "lodash";
import { convertTsToMilliseconds } from "components/util/TimezoneUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";

const fetchingHistorySelector = (state) => state.playlist.consolidatedHistory.fetching;
const consolidatedHistorySelector = (state) => state.playlist.consolidatedHistory.history;

const DEFAULT_EXPLORING_FILTERS = {
    day_time: "Day",
    extra_type: -1,
    days_in_past: 14,
    video_facing: -1,
};

const getCameraTypeObj = (streamsList) => {
    let trimmedList = [];
    _.forEach(streamsList, (stream) => {
        // every list item to be changed to remove "Visible"
        const temp = String(stream).replace("Visible", "").trim().toUpperCase();
        if (temp) {
            trimmedList.push(temp);
        }
    });

    // prioritise overhead corona over pantograph and overhead, as corona sessions can have also pantograph and overhead cameras but not the other way around
    if (_.includes(trimmedList, "OVERHEAD CORONA")) {
        return {
            name: "Corona",
            color: "#df6c43",
            fullList: streamsList,
        };
    }

    if (_.includes(trimmedList, "PANTOGRAPH")) {
        return {
            name: "Pantograph",
            color: "#4368df",
            fullList: streamsList,
        };
    }

    if (_.includes(trimmedList, "OVERHEAD")) {
        return {
            name: "Overhead",
            color: "#23399b",
            fullList: streamsList,
        };
    }

    if (_.includes(trimmedList, "FFV")) {
        return {
            name: "FFV",
            color: "#48945e",
            fullList: streamsList,
        };
    }

    return {
        name: "Other",
        color: "#999",
        fullList: streamsList,
    };
};

const calculateDirectionText = (tags) => {
    const forwardOrBackward = _.includes(tags, "Forward") ? "Forward" : _.includes(tags, "Backward") ? "Backward" : "Unknown";
    return forwardOrBackward;
};

const SessionListItem = ({ active, session, setExploringLocationSession, matchingType = false, selectedSectionFirstSeenTimestamp }) => {
    const dispatch = useDispatch();
    const [tooltipText, setTooltipText] = useState("Click to Copy");

    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(session.session_id);

        setTooltipText("Copied!");
        setTimeout(() => {
            setTooltipText("Click to Copy");
        }, 1200);
    };

    const recordingFromCurrentSessionText = useMemo(() => {
        const diffText = moment.unix(session.first_seen).from(moment.unix(selectedSectionFirstSeenTimestamp), true);
        const isAfter = moment.unix(session.first_seen).isAfter(moment.unix(selectedSectionFirstSeenTimestamp));

        return (
            <span style={{ cursor: "default" }}>
                <Tooltip
                    title={
                        isAfter ? (
                            <>
                                This session was recorded <strong>after</strong> the patrol session
                            </>
                        ) : (
                            <>
                                This session was recorded <strong>before</strong> the patrol session
                            </>
                        )
                    }
                    align={{ offset: [0, 5] }}
                    overlayStyle={{ minWidth: "fit-content" }}
                    arrowPointAtCenter>
                    <FontAwesomeIcon
                        style={{ paddingRight: "3px" }}
                        icon={faClock}
                        color={isAfter ? "#47c66b" : "#f9c27a"}
                    />
                    {diffText} <strong>{isAfter ? "after" : "before"}</strong> patrolling session
                </Tooltip>
            </span>
        );
    }, [session, selectedSectionFirstSeenTimestamp]);

    return (
        <Tooltip
            title={_.get(session, "included_as_selected", false) ? "Currently selected but not matching filters" : undefined}
            overlayStyle={{ minWidth: "fit-content" }}>
            <div
                key={session.session_id}
                className={`OlePatrolsExploringSessionsListItem ${active ? "Selected" : ""} ${_.get(session, "included_as_selected", false) ? "IncludedAsSelected" : ""}`}
                onClick={() => {
                    if (active) {
                        setExploringLocationSession(null);
                        return;
                    }

                    setExploringLocationSession(null); // clear current session while loading new one
                    dispatch(getSessionData(session.session_id))
                        .then((sessionData) => {
                            setExploringLocationSession(sessionData);
                        })
                        .catch((e) => {
                            setExploringLocationSession(null);
                        });
                }}>
                <Tooltip title={tooltipText}>
                    <div
                        className="OlePatrolsExploringSessionsListItemSessionID"
                        onClick={handleCopy}
                        style={{ cursor: "pointer" }}>
                        #{session.session_id}
                    </div>
                </Tooltip>
                <div className="OlePatrolsExploringSessionsListItemMain">
                    <div className="OlePatrolsExploringSessionsListItemTitle">{session.name}</div>
                    <div className="OlePatrolsExploringSessionsListItemInfoPane">
                        <div className="OlePatrolsExploringSessionsListItemDate">
                            <div className="OlePatrolsExploringSessionsListItemDateTime">{moment(session.timestamp).format("DD MMM YYYY HH:mm")}</div>
                            <div className="OlePatrolsExploringSessionsListItemDateFromNow">{recordingFromCurrentSessionText}</div>
                        </div>
                    </div>
                </div>
                <div className="OlePatrolsExploringSessionsListItemEnd">
                    {matchingType && (
                        <div
                            className="OlePatrolsExploringSessionsListItemCol"
                            style={{ minWidth: "82px" }}>
                            <div> </div>
                            <div className={`OlePatrolsExploringSessionsListItemTag BestMatch`}>
                                <Tooltip
                                    title={matchingType}
                                    overlayStyle={{ minWidth: "fit-content" }}>
                                    Best Match
                                </Tooltip>
                            </div>
                        </div>
                    )}

                    <div
                        className="OlePatrolsExploringSessionsListItemCol"
                        style={{ minWidth: "82px" }}>
                        <div>{`Video Type${_.get(session, ["video_type_obj", "fullList"], []).length > 1 ? "s" : ""}:`}</div>
                        <div
                            className={`OlePatrolsExploringSessionsListItemTag VideoType`}
                            style={{ backgroundColor: _.get(session, ["video_type_obj", "color"], "#999") }}>
                            <Tooltip title={_.join(_.get(session, ["video_type_obj", "fullList"], []), ", ")}>
                                {_.get(session, ["video_type_obj", "name"], "---")}
                            </Tooltip>
                            {_.get(session, ["video_type_obj", "fullList"], []).length > 1 && (
                                <div className="OlePatrolsExploringSessionsListItemBadge">{_.get(session, ["video_type_obj", "fullList"], []).length}</div>
                            )}
                        </div>
                    </div>

                    <div
                        className="OlePatrolsExploringSessionsListItemCol"
                        style={{ minWidth: "72px" }}>
                        <div>View:</div>
                        <div className={`OlePatrolsExploringSessionsListItemTag ${_.get(session, "video_facing_the_same_as_patrol") ? "Match" : "Opposite"}`}>
                            <Tooltip title={_.get(session, "video_facing_the_same_as_patrol") ? "The same view as patrol" : "Opposite view to patrol"}>
                                {_.get(session, "video_facing_the_same_as_patrol") ? "The same" : "Opposite"}
                            </Tooltip>
                        </div>
                    </div>

                    <div
                        className="OlePatrolsExploringSessionsListItemCol"
                        style={{ minWidth: "45px" }}>
                        <div>Track:</div>
                        <div className={`OlePatrolsExploringSessionsListItemTag ${_.get(session, "the_same_track_as_patrol") ? "Match" : ""}`}>
                            <Tooltip title={_.get(session, "the_same_track_as_patrol") ? "The same as patrol" : "Different from patrol"}>
                                {session.sub_position}
                            </Tooltip>
                        </div>
                    </div>

                    <div
                        className="OlePatrolsExploringSessionsListItemCol"
                        style={{ minWidth: "70px" }}>
                        <div>Direction:</div>
                        <div className="OlePatrolsExploringSessionsListItemTag">{_.get(session, "direction_text")}</div>
                    </div>

                    <div
                        className="OlePatrolsExploringSessionsListItemCol"
                        style={{ minWidth: "75px" }}>
                        <div>Bearing:</div>
                        <div className="OlePatrolsExploringSessionsListItemTag">{_.get(session, "bearing_text")}</div>
                    </div>
                </div>
            </div>
        </Tooltip>
    );
};

const ExploringOlePatrolComponent = ({ selectedSection, routePosition, session, setExploringLocationSessionData = () => {} }) => {
    const dispatch = useDispatch();
    const itemRefs = useRef({});

    const fetchingHistory = useSelector(fetchingHistorySelector);
    const consolidatedHistoryRaw = useSelector(consolidatedHistorySelector);

    const [exploringLocationBestSession, setExploringLocationBestSession] = useState(null);
    const [exploringLocationSession, setExploringLocationSession] = useState(null);
    const [matchingType, setMatchingType] = useState(null);
    const [exploringLocationFilters, setExploringLocationFilters] = useState(DEFAULT_EXPLORING_FILTERS);
    const [findingBestSession, setFindingBestSession] = useState(false);

    const mainPatrolSessionID = useMemo(() => (session ? session.id : null), [session]);

    const availableBearings = useMemo(() => {
        let bearings = getBearingOptions(consolidatedHistoryRaw);
        return bearings;
    }, [consolidatedHistoryRaw]);

    const currentSessionFromHistory = useMemo(() => {
        if (!consolidatedHistoryRaw || !mainPatrolSessionID) return null;
        const selectedSessionIndex = _.findIndex(consolidatedHistoryRaw, (item) => item.session_id === parseInt(mainPatrolSessionID));
        if (selectedSessionIndex === -1) return null;

        return consolidatedHistoryRaw[selectedSessionIndex];
    }, [consolidatedHistoryRaw, mainPatrolSessionID]);

    const currentSessionDirection = useMemo(() => {
        if (!currentSessionFromHistory) return null;
        return calculateDirectionText(currentSessionFromHistory.session_tags);
    }, [currentSessionFromHistory]);

    const currentSessionPositionBearing = useMemo(() => {
        if (!currentSessionFromHistory) return null;
        return calculateBearingText(_.get(currentSessionFromHistory, "bearing", null));
    }, [currentSessionFromHistory]);

    const currentSessionPositionTrack = useMemo(() => {
        if (!currentSessionFromHistory) return null;
        return _.get(currentSessionFromHistory, "sub_position", null);
    }, [currentSessionFromHistory]);

    const oppositeCurrentSessionDirection = useMemo(() => {
        return currentSessionDirection === "Forward" ? "Backward" : "Forward";
    }, [currentSessionDirection]);

    const oppositeCurrentSessionBearing = useMemo(() => {
        const newBearingIndex = _.findIndex(availableBearings, (b) => b !== currentSessionPositionBearing);
        return availableBearings[newBearingIndex];
    }, [availableBearings, currentSessionPositionBearing]);

    const consolidatedHistory = useMemo(() => {
        let sessionsObject = {};
        let list = [];

        _.forEach(consolidatedHistoryRaw, (item) => {
            if (!sessionsObject[item.session_id]) {
                sessionsObject[item.session_id] = [];
            }
            sessionsObject[item.session_id].push(item);
        });

        _.forEach(sessionsObject, (item) => {
            const firstItem = item[0];

            const streams = _.map(item, (stream) => {
                return _.get(stream, ["stream_info", "info", "type"], "Unknown");
            });
            const bearingText = calculateBearingText(firstItem.bearing);
            const directionText = calculateDirectionText(firstItem.session_tags);
            const videTypesObj = getCameraTypeObj(_.uniq(streams));

            list.push({
                ...firstItem,
                bearing_text: bearingText,
                direction_text: directionText,
                video_type_obj: videTypesObj,
                video_facing_the_same_as_patrol:
                    currentSessionPositionBearing && currentSessionDirection
                        ? (bearingText === currentSessionPositionBearing && directionText === currentSessionDirection) ||
                          (bearingText === oppositeCurrentSessionBearing && directionText === oppositeCurrentSessionDirection)
                        : null,
                the_same_track_as_patrol: currentSessionPositionTrack ? firstItem.sub_position === currentSessionPositionTrack : null,
            });
        });

        return list;
    }, [
        consolidatedHistoryRaw,
        currentSessionPositionBearing,
        currentSessionDirection,
        currentSessionPositionTrack,
        oppositeCurrentSessionBearing,
        oppositeCurrentSessionDirection,
    ]);

    // when fetching history is done, we want to check for historical sessions and based on other factors select one
    useEffect(() => {
        if (exploringLocationBestSession) {
            // in this case we already have a session selected as the best match, so we can just
            // want to make sure it is loaded in the player
            setExploringLocationSession(exploringLocationBestSession);
            return;
        }

        if (!fetchingHistory && consolidatedHistory && consolidatedHistory.length > 0) {
            // logic to select a session based on other factors
            let match = null;
            let matchingType = null;

            if (!session || !session.route_name) {
                return;
            }

            setFindingBestSession(true);

            // MATCHING LOGIC STEP 1: (session from the same train at the same date and time)
            // check if there is a session from the same train at the same date and time (this would be something
            // like VIU with Strex where it is on the same train but two different sessions are created)
            const selectedSessionFIrstSeenTS = convertTsToMilliseconds(_.get(session, "first_seen", null));

            // if strex both sessions should start at the same time, so we can check for sessions with the same
            // first seen timestamp and then look at their route name to confirm if it is the same session or not
            const historyFromTheSameFirstSeenTs = _.filter(consolidatedHistory, (item) => {
                const isTheSameSession = item.session_id === session.id;
                if (isTheSameSession || item.video_facing_the_same_as_patrol === false || item.the_same_track_as_patrol === false) {
                    return false;
                }

                const itemFirstSeenTs = convertTsToMilliseconds(_.get(item, "first_seen", null));
                if (itemFirstSeenTs && selectedSessionFIrstSeenTS) {
                    const timeDiff = Math.abs(moment(selectedSessionFIrstSeenTS).diff(moment(itemFirstSeenTs), "minutes"));
                    return timeDiff === 0; // not more than 1 minute difference in start time
                }
                return false;
            });

            // if historyFromTheSameFirstSeenTs has sessions validate for their route name
            // strex on viu will have the same end of the route name
            _.forEach(historyFromTheSameFirstSeenTs, (item) => {
                const itemRouteName = _.get(item, "name", "").split(" ");
                const selectedSessionRouteName = _.get(session, "route_name", "").split(" ");
                const hasMatchingRouteNamePart = _.intersection(itemRouteName, selectedSessionRouteName);

                const _matchScore = hasMatchingRouteNamePart.length;
                if (_matchScore >= 5) {
                    match = item;
                }
            });

            if (match && !matchingType) {
                matchingType = "Session from the same train at the same time";
            }

            // MATCHING LOGIC STEP 2: (earliest session from the with the same direction and track as patrol)
            if (!match) {
                const sessionsWithTheSameView = _.filter(consolidatedHistory, (item) => {
                    const isTheSameSession = item.session_id === session.id;
                    if (isTheSameSession) {
                        return false;
                    }

                    // we want to prioritise sessions with the same view and track as patrol
                    if (item.video_facing_the_same_as_patrol && item.the_same_track_as_patrol && item.session_tags.includes("Day")) {
                        const itemFirstSeenTs = convertTsToMilliseconds(_.get(item, "first_seen", null));
                        // Check if first seen is not older than 2 weeks
                        const twoWeeksAgo = moment().subtract(14, "days");
                        if (itemFirstSeenTs && moment(itemFirstSeenTs).isBefore(twoWeeksAgo)) {
                            return false;
                        }
                        return true;
                    }

                    return false;
                });

                match = _.last(sessionsWithTheSameView); // take the earliest session with the same view and track as patrol
            }

            if (match && !matchingType) {
                matchingType = "Same session view and track as patrol";
            }

            // MATCHING LOGIC STEP 3: if all above matching logic did not find any session, we just take the session with the same view
            if (!match) {
                const sessionsWithTheSameViewOnly = _.filter(consolidatedHistory, (item) => {
                    const isTheSameSession = item.session_id === session.id;
                    if (isTheSameSession) {
                        return false;
                    }

                    if (item.video_facing_the_same_as_patrol && item.session_tags.includes("Day")) {
                        return true;
                    }

                    return false;
                });

                match = _.last(sessionsWithTheSameViewOnly); // take the earliest session with the same view as patrol
            }

            if (match && !matchingType) {
                matchingType = "Same view as patrol";
            }

            if (match) {
                setMatchingType(matchingType);
                setExploringLocationSession(null); // clear current session while loading new one
                dispatch(getSessionData(match.session_id))
                    .then((sessionData) => {
                        setExploringLocationSession(sessionData);
                        setExploringLocationBestSession(sessionData);
                    })
                    .catch((e) => {
                        setExploringLocationSession(null);
                    })
                    .finally(() => {
                        setFindingBestSession(false);
                    });

                return;
            } else {
                notification.info({
                    message: "No similar sessions found",
                    description: "We couldn't find any similar sessions for the selected patrol session.",
                });
            }

            setFindingBestSession(false);
        }
    }, [consolidatedHistory, dispatch, fetchingHistory, session, exploringLocationBestSession]);

    const filteredHistoryList = useMemo(() => {
        let newList = [];

        // filters to go here later....
        _.forEach(consolidatedHistory, (historyItem) => {
            let include = true;

            // exclude current session
            if (historyItem.session_id === parseInt(mainPatrolSessionID)) {
                include = false;
            }

            // Apply day_time filter
            if (include && exploringLocationFilters.day_time && exploringLocationFilters.day_time !== "Any") {
                if (!_.includes(historyItem.session_tags, exploringLocationFilters.day_time)) {
                    include = false;
                }
            }

            // apply video_facing filter
            if (include && exploringLocationFilters.video_facing && exploringLocationFilters.video_facing !== -1) {
                // const itemBearingText = calculateBearingText(historyItem.bearing);
                // const itemDirectionTest = calculateDirectionText(historyItem.session_tags);

                if (exploringLocationFilters.video_facing === "same") {
                    // facing same way as current session
                    include = historyItem.video_facing_the_same_as_patrol === true;
                } else if (exploringLocationFilters.video_facing === "opposite") {
                    // facing opposite way to current session
                    include = historyItem.video_facing_the_same_as_patrol === false;
                }
            }

            // Apply extra_type filter
            if (include && exploringLocationFilters.extra_type && exploringLocationFilters.extra_type !== -1) {
                if (!_.includes(_.get(historyItem, ["video_type_obj", "name"], ""), exploringLocationFilters.extra_type)) {
                    include = false;
                }
            }

            // days in past filter
            if (include && exploringLocationFilters.days_in_past && exploringLocationFilters.days_in_past > 0) {
                const daysInPastDate = new Date();
                daysInPastDate.setDate(daysInPastDate.getDate() - exploringLocationFilters.days_in_past);
                if (new Date(historyItem.timestamp) < daysInPastDate) {
                    include = false;
                }
            }

            if (include) {
                newList.push(historyItem);
            }

            // if not include and session is from exploringLocationSession add to session
            if (!include && exploringLocationSession && historyItem.session_id === exploringLocationSession.id) {
                newList.push({
                    ...historyItem,
                    included_as_selected: true,
                });
                return;
            }
        });

        newList = _.uniqBy(newList, (item) => item.session_id).reverse();

        return newList;
    }, [consolidatedHistory, mainPatrolSessionID, exploringLocationFilters, exploringLocationSession]);

    useEffect(() => {
        if (!exploringLocationSession || fetchingHistory) return;

        const delay = setTimeout(() => {
            const activeEl = itemRefs.current[exploringLocationSession.id];
            if (activeEl) {
                activeEl.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }
        }, 0);

        return () => clearTimeout(delay);
    }, [exploringLocationSession, fetchingHistory]);

    const allVideoTypes = useMemo(() => {
        const types = [];
        _.forEach(consolidatedHistory, (item) => {
            const videoType = _.get(item, ["video_type_obj", "name"], null);
            if (videoType && !types.includes(videoType)) {
                types.push(videoType);
            }
        });
        return types.sort();
    }, [consolidatedHistory]);

    return (
        <div className="OlePatrolsExploringMain">
            <div className="OlePatrolsExploringTopBar">
                <div className="OlePatrolsExploringTitle">Explore Location using different recordings</div>
            </div>
            <div className="OlePatrolsExploringContent">
                {(fetchingHistory || findingBestSession) && !exploringLocationSession && (
                    <div className="FindingBestSessionOverlay">
                        <div className="FindingBestSessionContent">
                            <OBCSpinner
                                color={"#e8dfff"}
                                size={80}
                            />
                            <p>Finding the best session match...</p>
                        </div>
                    </div>
                )}
                <div className="OlePatrolsExploringPlayer">
                    <StandAloneVideoOrImageComponent
                        selectedSession={exploringLocationSession}
                        positionFilter={selectedSection}
                        startPosition={routePosition}
                        onPositionChanged={setExploringLocationSessionData}
                        // loadingText={findingBestSession ? "Finding the best session match..." :  null}
                    />
                </div>

                <div className="OlePatrolsExploringSessionsComponent">
                    <div className="OlePatrolsExploringSessionsFilters">
                        <div className="Left">
                            {/* Time of Day */}
                            <div className="OlePatrolsExploringSessionsFiltersRow">
                                <div className="OlePatrolsExploringSessionsFiltersTitle">Time of Day</div>
                                <div className="OlePatrolsExploringSessionsFiltersInput">
                                    <Select
                                        placeholder="Filter day time"
                                        value={_.get(exploringLocationFilters, "day_time", "Day")}
                                        onChange={(value) => {
                                            setExploringLocationFilters((prevState) => {
                                                return {
                                                    ...prevState,
                                                    day_time: value,
                                                };
                                            });
                                        }}
                                        style={{ width: "75px" }}
                                        size="small">
                                        <Select.Option value="Any">Any</Select.Option>
                                        <Select.Option value="Day">Day</Select.Option>
                                        <Select.Option value="Night">Night</Select.Option>
                                    </Select>
                                </div>
                            </div>

                            {/* Video Filter */}
                            <div className="OlePatrolsExploringSessionsFiltersRow">
                                <div className="OlePatrolsExploringSessionsFiltersTitle">Video</div>
                                <div className="OlePatrolsExploringSessionsFiltersInput">
                                    <Select
                                        placeholder="Pantograph Only"
                                        value={_.get(exploringLocationFilters, "extra_type", false)}
                                        onChange={(value) => {
                                            setExploringLocationFilters((prevState) => {
                                                return {
                                                    ...prevState,
                                                    extra_type: value,
                                                };
                                            });
                                        }}
                                        dropdownMatchSelectWidth={false}
                                        size="small">
                                        <Select.Option value={-1}>Any</Select.Option>
                                        {_.map(allVideoTypes, (type) => (
                                            <Select.Option
                                                key={type}
                                                value={type}>
                                                {type}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                            {/* Session date range */}
                            <div className="OlePatrolsExploringSessionsFiltersRow">
                                <div className="OlePatrolsExploringSessionsFiltersTitle">Date Range</div>
                                <div className="OlePatrolsExploringSessionsFiltersInput">
                                    <Select
                                        placeholder="Pantograph Only"
                                        value={_.get(exploringLocationFilters, "days_in_past", -1)}
                                        onChange={(value) => {
                                            setExploringLocationFilters((prevState) => {
                                                return {
                                                    ...prevState,
                                                    days_in_past: value,
                                                };
                                            });
                                        }}
                                        dropdownMatchSelectWidth={false}
                                        size="small">
                                        <Select.Option value={-1}>Any</Select.Option>
                                        <Select.Option value={7}>Last 7 Days</Select.Option>
                                        <Select.Option value={14}>Last 14 Days</Select.Option>
                                    </Select>
                                </div>
                            </div>

                            {/* Video Facing */}
                            <div className="OlePatrolsExploringSessionsFiltersRow">
                                <div className="OlePatrolsExploringSessionsFiltersTitle">View</div>
                                <div className="OlePatrolsExploringSessionsFiltersInput">
                                    <Select
                                        showSearch
                                        placeholder="Video Facing"
                                        value={_.get(exploringLocationFilters, "video_facing", -1)}
                                        onChange={(value) => {
                                            setExploringLocationFilters((prevState) => {
                                                return {
                                                    ...prevState,
                                                    video_facing: value,
                                                };
                                            });
                                        }}
                                        dropdownMatchSelectWidth={false}
                                        size="small">
                                        <Select.Option value={-1}>Any</Select.Option>
                                        <Select.Option value={"same"}>The same as patrol</Select.Option>
                                        <Select.Option value={"opposite"}>Opposite to patrol</Select.Option>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        {/* Right side */}
                        <div className="Right">
                            <div className="OlePatrolsExploringPlayerControls">
                                <div></div>
                            </div>
                        </div>
                    </div>

                    <div className="OlePatrolsExploringSessionsListMain">
                        {fetchingHistory ? (
                            <div className="LoadingSpinnerCenter">
                                <div>
                                    <OBCSpinner
                                        color={"#e8dfff"}
                                        size={60}
                                    />
                                </div>
                                <p>Loading nearby sessions...</p>
                            </div>
                        ) : (
                            <div className="OlePatrolsExploringSessionsList">
                                {filteredHistoryList.length ? (
                                    _.map(filteredHistoryList, (_session) => {
                                        const isBestMatchingSession = _session.session_id === parseInt(_.get(exploringLocationBestSession, "id", -1));

                                        const isActive = exploringLocationSession && exploringLocationSession.id === _session.session_id;

                                        return (
                                            <div
                                                key={_session.session_id}
                                                ref={(el) => {
                                                    if (el) itemRefs.current[_session.session_id] = el;
                                                }}>
                                                <SessionListItem
                                                    active={isActive}
                                                    session={_session}
                                                    setExploringLocationSession={setExploringLocationSession}
                                                    matchingType={isBestMatchingSession ? matchingType : null}
                                                    selectedSectionFirstSeenTimestamp={session?.first_seen}
                                                />
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="NoSessionsFound">
                                        <div>No sessions found matching the selected filters.</div>
                                        <div>
                                            Total number of sessions: <strong>{Object.keys(consolidatedHistory).length}</strong>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default ExploringOlePatrolComponent;
